import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { LuMailCheck } from 'react-icons/lu';
import { AuthLayout } from '../../app/AuthLayout';
import { Button } from '../../ui/Button';
import { Field, Input, PasswordInput } from '../../ui/Form';
import { Alert, EmptyState, Spinner } from '../../ui/Feedback';
import { PasswordRules, isStrongPassword } from './PasswordRules';
import { useDocumentTitle, useStrings } from '../../tenant/TenantProvider';
import { useTenantSettings } from '../../tenant/useTenantSettings';
import { apiRequest } from '../../lib/api';

const USERNAME_PATTERN = /^[A-Za-z0-9._]{3,30}$/;

export function SignupPage() {
  const t = useStrings();
  const settings = useTenantSettings();
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  useDocumentTitle(t('auth.signupTitle'));
  if (settings && !settings.signupEnabled) return <Navigate to="/login" replace />;

  const usernameInvalid = form.username.length > 0 && !USERNAME_PATTERN.test(form.username);
  const canSubmit = USERNAME_PATTERN.test(form.username) && form.email && isStrongPassword(form.password);

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const response = await apiRequest('/auth/v2/signup', { method: 'POST', body: form });
    setSubmitting(false);
    if (response.success) setSent(true);
    else setError(response.error);
  };

  const footer = (
    <>
      {t('auth.haveAccount')}{' '}
      <Link to="/login" className="text-link">
        {t('auth.signInLink')}
      </Link>
    </>
  );

  if (!settings) {
    return (
      <AuthLayout title={t('auth.signupTitle')} subtitle={t('auth.signupSubtitle')} footer={footer}>
        <div className="auth__loading">
          <Spinner label={t('common.loading')} />
        </div>
      </AuthLayout>
    );
  }

  if (sent) {
    return (
      <AuthLayout title={t('auth.signupSentTitle')} footer={footer}>
        <EmptyState icon={LuMailCheck} title={t('auth.signupSentText', { email: form.email })} className="auth__sent" />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.signupTitle')} subtitle={t('auth.signupSubtitle')} footer={footer}>
      {error && <Alert tone="danger">{error}</Alert>}
      <form className="auth__form" onSubmit={submit} noValidate>
        <Field label={t('auth.username')} htmlFor="signup-username" hint={t('auth.usernameHint')} error={usernameInvalid ? t('auth.usernameHint') : null}>
          <Input
            id="signup-username"
            size="lg"
            autoComplete="username"
            autoFocus
            invalid={usernameInvalid}
            value={form.username}
            onChange={(event) => setForm({ ...form, username: event.target.value })}
          />
        </Field>
        <Field label={t('auth.email')} htmlFor="signup-email">
          <Input id="signup-email" type="email" size="lg" autoComplete="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
        </Field>
        <Field label={t('auth.password')} htmlFor="signup-password">
          <PasswordInput
            id="signup-password"
            size="lg"
            autoComplete="new-password"
            aria-describedby="signup-rules"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
            showLabel={t('auth.showPassword')}
            hideLabel={t('auth.hidePassword')}
          />
        </Field>
        <PasswordRules value={form.password} id="signup-rules" />
        <Button type="submit" variant="primary" size="lg" block loading={submitting} disabled={!canSubmit}>
          {submitting ? t('auth.creatingAccount') : t('auth.createAccount')}
        </Button>
      </form>
    </AuthLayout>
  );
}
